import './ScheduleVarianceReport.scss';

import { CButton, CCol, CModal, CModalBody, CModalFooter, CModalHeader, CRow } from "@coreui/react";
import React, { useContext, useEffect, useState } from "react";
import {
  approveVarianceReport,
  checkIfDailyScheduleSet,
  getVarianceReport,
  resetVarianceReport,
  updateVarianceReportEntryNote,
} from "../../../api/invoiceApi";

import AKTable from "../../common/table/AKTable";
import ExcelDocUploader from '../../common/excel-uploader/ExcelDocUploader';
import FormInput from "../../common/form/inputs/FormInput";
import Loading from "../../common/Loading";
import UserContext from "../../../data/UserContext";
import { VarianceReport } from "../../../api/types/VarianceReport";
import { VarianceReportEntry } from "../../../api/types/VarianceReportEntry";
import { importSubcontractorHours } from '../../../api/subcontractorApi';
import { inputTypes } from "../../../constants/input-types";
import { optionSortMethods } from "../../../constants/optionSortMethods";
import { optionTypes } from "../../../constants/optionTypes";
import { roleTypes } from "../../../constants/role-types";
import { useHistory } from "react-router";

type ReportRow = {
  complexName: string;
  plannedEmployeeName: string;
  plannedStartTime?: Date;
  plannedEndTime?: Date;
  reportedEmployeeName: string;
  startVariance: number;
  endVariance: number;
  isSubcontractor: boolean
} & VarianceReportEntry;

export default function ScheduleVarianceReport() {
  const user = useContext(UserContext);
  const history = useHistory();
  const [varianceReport, setVarianceReport] = useState<VarianceReport>();

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const date =
    history.location.state && history.location.state.date
      ? history.location.state.date
      : yesterday.toISOString();
  const [workDate, setWorkDate] = useState(new Date(date));
  const [dailyScheduleSet, setDailyScheduleSet] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdmin = user?.roles.includes(roleTypes.ADMIN) ?? false;

  useEffect(() => {
    let retrieved = false;

    const fetchData = async () => {
      if (workDate) {
        const hasSchedule = await checkIfDailyScheduleSet(workDate);
        if (retrieved) return;
        setDailyScheduleSet(hasSchedule);

        if (hasSchedule) {
          const t = await getVarianceReport(workDate);
          if (retrieved) return;
          setVarianceReport(t);
        }
      }
    };

    setVarianceReport(undefined);
    fetchData().catch(console.error);

    return () => { retrieved = true; };
  }, [workDate]);

  const handleScheduledDateChange = async ({ date }: { date: string }) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    setWorkDate(new Date(date));
  };

  const handleApproveReportClick = () => {
    if (varianceReport) {
      approveVarianceReport(varianceReport?.id).then(() =>
        // set the schedule date to refresh the schedule data
        setWorkDate(new Date(workDate)),
      );
    }
  };
  
  const resetReport = () => {
    setVarianceReport(undefined);
    setShowResetModal(false);
    resetVarianceReport(workDate).then((report) => {
      setVarianceReport(report);
    });
  }

  const handleImportSubcontractorHours = async (formDate: FormData) => {
    await importSubcontractorHours(formDate);
    // set the schedule date to refresh the schedule data
    setWorkDate(new Date(workDate));
  }

  return (
    <>
      <div className="card animated-card view-schedule-wrapper">
        <div className="card-header ak-sticky-header">
          <CRow className="justify-content-between w-100">
            <CCol sm={12} md={4}>
              <FormInput
                inputsPerRow={2}
                onChange={handleScheduledDateChange}
                value={workDate}
                label="Work Date"
                type={inputTypes.DATE}
                editable={true}
                maxDate={yesterday}
              />
            </CCol>

            <CCol sm={12} md={4}>
              {varianceReport && isAdmin && (
                <FormInput
                  className={"mt-4"}
                  type={inputTypes.BUTTONPRIMARY}
                  noTopMargin
                  inputsPerRow={2}
                  editable={true}
                  label={"Reset Report"}
                  onClick={() => setShowResetModal(true)}
                />
              )}
              <FormInput
                  className={"mt-4"}
                  type={inputTypes.BUTTONPRIMARY}
                  noTopMargin
                  inputsPerRow={2}
                  editable={true}
                  label={"Import Subcontractor Hours"}
                  onClick={() => setShowImportModal(true)}
                />
              {varianceReport && (
                <ApproveVarianceReportButton
                  report={varianceReport}
                  handleApproveReportClick={handleApproveReportClick}
                />
              )}
            </CCol>
            <CCol sm={12} md={4}>
              <h3 className="text-center font-weight-bold text-uppercase py-4 text-nowrap">
                Variance Report For{" "}
                {`${
                  workDate.getMonth() + 1
                }/${workDate.getDate()}/${workDate.getFullYear()}`}
              </h3>
            </CCol>
          </CRow>
        </div>
        {!dailyScheduleSet && (
          <div className="d-flex justify-content-center align-items-center p-5">
            <h4>No daily schedule set for this date.</h4>
          </div>
        )}
        {varianceReport && (
          <VarianceReportTable
            varianceReport={varianceReport}
            key={workDate.getDate()}
          />
        )}
        {dailyScheduleSet && !varianceReport && (
          <Loading />
        )}
      </div>
      <CModal
        show={showResetModal}
        onClose={() => setShowResetModal(false)}
      >
        <CModalHeader>
          <h5>Reset Variance Report</h5>
        </CModalHeader>
        <CModalBody>
          <p>
            Are you sure you want to reset the variance report? The report will be regenerated and any changes made to the report will be lost.
          </p>
        </CModalBody>
        <CModalFooter>
          <CButton 
           className={"btn akp-btn-primary"}
           onClick={() => setShowResetModal(false)}>
            No
          </CButton>
          <CButton 
           className={"btn akp-btn-primary"}
           onClick={resetReport}>
            Yes
          </CButton>
        </CModalFooter>
      </CModal>
      <CModal
        show={showImportModal}
        onClose={() => setShowImportModal(false)}
      >
        <CModalHeader>
          <h5>Import Subcontractor Hours</h5>
        </CModalHeader>
        <CModalBody>
          <ExcelDocUploader
            handleImport={(formData) => handleImportSubcontractorHours(formData)}
            setShowModal={setShowImportModal}
            forceUpdate={() => {}}
            setLoadingParent={() => {}}
          />
        </CModalBody>
        <CModalFooter>
          <button className="btn btn-secondary" onClick={() => setShowImportModal(false)}>Cancel</button>
        </CModalFooter>
      </CModal>
    </>
  );
}

function ApproveVarianceReportButton({
  report,
  handleApproveReportClick,
}: {
  report?: VarianceReport;
  handleApproveReportClick: () => void;
}) {
  if (!report) {
    return undefined;
  }

  if (report.approvalDate) {
    return (
      <p>
        Approved on {new Date(report.approvalDate).toLocaleString()} by{" "}
        {report.approvingEmployee?.firstName}{" "}
        {report.approvingEmployee?.lastName}.
      </p>
    );
  }
  return (
    <FormInput
      className={"mt-4"}
      type={inputTypes.BUTTONPRIMARY}
      noTopMargin
      inputsPerRow={2}
      editable={true}
      label={"Approve Report"}
      onClick={handleApproveReportClick}
    />
  );
}

function VarianceReportTable({
  varianceReport,
  ...akTableProps
}: {
  varianceReport: VarianceReport;
}) {
  const [columnOptionState, setColumnOptionState] = useState([
    {
      header: "Complex",
      key: "complexName",
      show: true,
      default: true,
      sortMethod: optionSortMethods.STRING,
    },
    {
      header: "Planned Employee",
      key: "plannedEmployeeName",
      show: true,
      default: true,
      sortMethod: optionSortMethods.STRING,
    },
    {
      header: "Planned Start",
      key: "plannedStartTime",
      show: true,
      default: true,
      sortMethod: optionSortMethods.TIME_STRING,
      type: optionTypes.TIME,
    },
    {
      header: "Planned End",
      key: "plannedEndTime",
      show: true,
      default: true,
      sortMethod: optionSortMethods.TIME_STRING,
      type: optionTypes.TIME,
    },
    {
      header: "Reported Employee",
      key: "reportedEmployeeName",
      show: true,
      default: true,
      sortMethod: optionSortMethods.STRING,
    },
    {
      header: "Reported Start",
      key: "reportedStartTime",
      show: true,
      default: true,
      sortMethod: optionSortMethods.TIME_STRING,
      type: optionTypes.TIME,
    },
    {
      header: "Reported End",
      key: "reportedEndTime",
      show: true,
      default: true,
      sortMethod: optionSortMethods.TIME_STRING,
      type: optionTypes.TIME,
    },
    {
      header: "Start Variance",
      key: "startVariance",
      show: true,
      default: true,
      sortMethod: optionSortMethods.NUMBER,
    },
    {
      header: "End Variance",
      key: "endVariance",
      show: true,
      default: true,
      sortMethod: optionSortMethods.NUMBER,
    },
    {
      header: "Total Variance",
      key: "totalVariance",
      show: true,
      default: true,
      sortMethod: optionSortMethods.NUMBER
    },
    {
      header: "Notes",
      key: "notes",
      show: true,
      default: true,
      sortMethod: optionSortMethods.STRING,
    },
  ]);
  const [rows, setRows] = useState<ReportRow[]>(
    varianceReport?.entries.map((e) => {
      return {
        ...e,
        complexName: e.dailyScheduleEntry?.complexName || e.reportedComplexName,
        plannedEmployeeName: e.dailyScheduleEntry
          ? `${e.dailyScheduleEntry?.employeeFirstName} ${e.dailyScheduleEntry?.employeeLastName}`
          : "",
        plannedStartTime: e.overriddenStartTime ?? e.dailyScheduleEntry?.startTime,
        plannedEndTime: e.overriddenEndTime ?? e.dailyScheduleEntry?.endTime,
        reportedEmployeeName: e.reportedEmployee
          ? `${e.reportedEmployee.firstName} ${e.reportedEmployee.lastName}`
          : `${e.reportedPayrollId}`,
        startVariance: Math.round(e.startVarianceInSeconds / 60),
        endVariance: Math.round(e.endVarianceInSeconds / 60),
        totalVariance: Math.round(e.totalVarianceInSeconds / 60),
        isSubcontractor: e.reportedEmployee?.roles.includes(roleTypes.SUBCONTRACTOR)
      };
    }) ?? [],
  );
  const [isAscendingState, setIsisAscendingState] = useState(false);
  const [orderKeyState, setOrderKeyState] = useState("");
  const [queryStringState, setQueryStringState] = useState("");
  const [searchKeysState, setSearchKeysState] = useState();
  const [includeInactiveState, setIncludeInactiveState] = useState(false);
  const [loading, setLoading] = useState(false);
  const [forceRerenderTable, setForceRerenderTable] = useState(false);

  const handleInlineEdit = async <K extends keyof ReportRow>(
    value: ReportRow[K],
    item: { index: number; id: string | number },
    key: K,
    event: any,
  ) => {
    rows[item.index][key] = value;
    setRows([...rows]);
    if (key == "notes") {
      await updateVarianceReportEntryNote(item.id, value);
    }
    setForceRerenderTable(true);
  };

  const handleInlineFormat = (
    event: React.ChangeEvent<HTMLInputElement>,
    key: keyof ReportRow,
  ) => {
    switch (key) {
      case "startVarianceInSeconds":
        return event.target.value;
      // case "startTime":
      // case "endTime":
      // case "employee":
      //   return event;
      default:
        return event.target.value;
    }
  };

  const handleInlineValidate = () => {
    return true;
  };

  // lock the cell if the schedule has already been approved
  const handleShouldLockCell = () => !!varianceReport?.approvalDate;

  const getRowClass = (rowData: ReportRow) => {
    if (rowData.isSubcontractor) {
      return 'subcontractor-entry'
    } else if (rowData.missedShift) {
      return 'missing-time-entry';
    }

    return '';
  }

  return (
    <AKTable
      data={rows}
      setData={setRows}
      options={columnOptionState}
      setOptions={setColumnOptionState}
      loading={loading}
      orderKey={orderKeyState}
      setOrderKey={setOrderKeyState}
      isAscending={isAscendingState}
      setIsAscending={setIsisAscendingState}
      numberOfPages={1}
      onInlineEdit={handleInlineEdit}
      onInlineFormat={handleInlineFormat}
      onInlineValidate={handleInlineValidate}
      handleShouldLockCell={handleShouldLockCell}
      ignoreInlineKeys={[
        "complexName",
        "plannedEmployeeName",
        "plannedStartTime",
        "plannedEndTime",
        "reportedEmployeeName",
        "reportedStartTime",
        "reportedEndTime",
        "startVariance",
        "endVariance",
        "totalVariance"
      ]}
      defaultOrderKey="complexName"
      defaultIsAscending={true}
      getRowClass={getRowClass}
      {...akTableProps}
    />
  );
}
